import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type {
  AddChangeSetNewRowInput,
  AddChangeSetNewRowResponse,
  DeleteChangeSetNewRowInput,
  DeleteChangeSetNewRowResponse,
  ListMetadataChangeSetsResponse,
  ListMetadataChangeSetEventsResponse,
  ListMetadataChangeSetVersionsResponse,
  RestoreMetadataChangeSetVersionInput,
  RestoreMetadataChangeSetVersionResponse,
  StageSelectedChangesInput,
  StageAllChangesInput,
  StageChangesResponse,
  StageFromItemListInput,
  StageDataLinkEditsInput,
  StageDataLinkEditsResponse,
  StageRawDataEditsInput,
  StageRawDataEditsResponse,
  UpdateChangeSetNewRowsInput,
  UpdateChangeSetNewRowsResponse,
  UpdateMetadataChangeSetInput,
  UpdateMetadataChangeSetResponse,
  DeleteMetadataChangeSetResponse,
} from '@erp/rpc';

export function useMetadataChangeSets(enabled = true) {
  const query = useQuery({
    ...orpc.inventoryManagement.metadataChangeSets.list.queryOptions({
      input: {},
    }),
    enabled,
  });

  return {
    ...query,
    changeSets: query.data?.changeSets ?? [],
  } satisfies typeof query & {
    data?: ListMetadataChangeSetsResponse;
    changeSets: ListMetadataChangeSetsResponse['changeSets'];
  };
}

/** One change set's metadata (name, description, status). Its contents are
 * browsed through the changeSetId-scoped catalog endpoints. */
export function useMetadataChangeSet(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.inventoryManagement.metadataChangeSets.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return { ...query, changeSet: query.data?.changeSet ?? null };
}

/**
 * A change set's history events (newest first): change-set-level actions —
 * creation, detail/status/assignee changes, staged-edit saves — with actors.
 */
export function useMetadataChangeSetEvents(id: string | undefined) {
  const query = useQuery({
    ...orpc.inventoryManagement.metadataChangeSets.listEvents.queryOptions({
      input: id ? { changeSetId: id } : skipToken,
    }),
    enabled: Boolean(id),
  });

  return {
    ...query,
    events: (query.data?.events ??
      []) satisfies ListMetadataChangeSetEventsResponse['events'],
  };
}

/**
 * A change set's saved versions (newest first): the states its staged edits
 * have been in, with the live one flagged.
 */
export function useMetadataChangeSetVersions(id: string | undefined) {
  const query = useQuery({
    ...orpc.inventoryManagement.metadataChangeSets.listVersions.queryOptions({
      input: id ? { changeSetId: id } : skipToken,
    }),
    enabled: Boolean(id),
  });

  return {
    ...query,
    versions: (query.data?.versions ??
      []) satisfies ListMetadataChangeSetVersionsResponse['versions'],
  };
}

/**
 * Restore a change set's staged edits to a previous version. Every view that
 * renders the staged overlays is invalidated, along with the sidebar's version
 * and event lists.
 */
export function useRestoreMetadataChangeSetVersion() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    RestoreMetadataChangeSetVersionResponse,
    Error,
    RestoreMetadataChangeSetVersionInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.restoreVersion(input),
    onSuccess: (data) => {
      const staleKeys = [
        orpc.inventoryManagement.products.listAs400Export.key(),
        orpc.inventoryManagement.products.listCatalogAs400View.key(),
        orpc.inventoryManagement.products.listRawDataRecords.key(),
        orpc.inventoryManagement.metadataChangeSets.listVersions.key(),
        orpc.inventoryManagement.metadataChangeSets.listEvents.key(),
      ];
      for (const queryKey of staleKeys) {
        void queryClient.invalidateQueries({ queryKey });
      }
      showToast({
        title: 'Version restored',
        message: `The review was restored to version ${data.versionNumber}.`,
        color: 'success',
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to restore that version',
        color: 'error',
      });
    },
  });
}

/** Invalidate the change-sets list so a newly staged set shows in Reviews. */
function useInvalidateChangeSetsList() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.metadataChangeSets.list.queryKey({
        input: {},
      }),
    });
}

/** Stage an explicit selection of catalog products as a new change set. */
export function useStageSelectedChanges() {
  const invalidate = useInvalidateChangeSetsList();
  return useMutation<StageChangesResponse, Error, StageSelectedChangesInput>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.stageSelected(input),
    onSuccess: () => void invalidate(),
  });
}

/** Stage every product matching the current catalog search + filters. */
export function useStageAllChanges() {
  const invalidate = useInvalidateChangeSetsList();
  return useMutation<StageChangesResponse, Error, StageAllChangesInput>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.stageAll(input),
    onSuccess: () => void invalidate(),
  });
}

/** Stage an org-shared item list's products as a new change set. The list's
 * product keys are resolved server-side — no large payload leaves the client. */
export function useStageFromItemList() {
  const invalidate = useInvalidateChangeSetsList();
  return useMutation<StageChangesResponse, Error, StageFromItemListInput>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.stageFromItemList(input),
    onSuccess: () => void invalidate(),
  });
}

/**
 * Stage inline edits made in the review page's Export tab as data-link
 * changes. Invalidates the change-set-scoped export view so saved cells
 * re-render from their staged values.
 */
export function useStageDataLinkEdits() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    StageDataLinkEditsResponse,
    Error,
    StageDataLinkEditsInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.stageDataLinkEdits(
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.products.listAs400Export.key(),
      });
      // The Search tab's expanded raw-data records render the same staged
      // overlays, so refresh them too.
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.products.listRawDataRecords.key(),
      });
      // The save appends a change-set history event and a new version.
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.metadataChangeSets.listEvents.key(),
      });
      void queryClient.invalidateQueries({
        queryKey:
          orpc.inventoryManagement.metadataChangeSets.listVersions.key(),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to save the staged edits',
        color: 'error',
      });
    },
  });
}

/**
 * Stage inline edits made in the review page's Search-tab AS/400 view, where
 * each edited row is one ingest record. Invalidates the AS/400 view and the
 * expanded raw-data records so saved cells re-render from their staged values.
 */
export function useStageRawDataEdits() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<StageRawDataEditsResponse, Error, StageRawDataEditsInput>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.stageRawDataEdits(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.products.listCatalogAs400View.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.products.listRawDataRecords.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.metadataChangeSets.listEvents.key(),
      });
      void queryClient.invalidateQueries({
        queryKey:
          orpc.inventoryManagement.metadataChangeSets.listVersions.key(),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to save the staged edits',
        color: 'error',
      });
    },
  });
}

/** Refresh everything a new-row change touches: the scoped export view (the
 * rows render there) and the sidebar's history — events and versions, since
 * every new-row write also records a version. */
function useInvalidateNewRowViews() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.products.listAs400Export.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.metadataChangeSets.listEvents.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.metadataChangeSets.listVersions.key(),
    });
  };
}

/** Author a net-new worksheet row on a change set. */
export function useAddChangeSetNewRow() {
  const invalidate = useInvalidateNewRowViews();
  const { showToast } = useToast();

  return useMutation<
    AddChangeSetNewRowResponse,
    Error,
    AddChangeSetNewRowInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.addNewRow(input),
    onSuccess: invalidate,
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to add the row',
        color: 'error',
      });
    },
  });
}

/**
 * Merge cell edits into a change set's new rows. Part of the export grid's
 * save flow, which owns the success toast — only failures surface here.
 */
export function useUpdateChangeSetNewRows() {
  const invalidate = useInvalidateNewRowViews();
  const { showToast } = useToast();

  return useMutation<
    UpdateChangeSetNewRowsResponse,
    Error,
    UpdateChangeSetNewRowsInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.updateNewRows(input),
    onSuccess: invalidate,
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to save the new-row edits',
        color: 'error',
      });
    },
  });
}

/** Remove a new row from a change set. */
export function useDeleteChangeSetNewRow() {
  const invalidate = useInvalidateNewRowViews();
  const { showToast } = useToast();

  return useMutation<
    DeleteChangeSetNewRowResponse,
    Error,
    DeleteChangeSetNewRowInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.deleteNewRow(input),
    onSuccess: invalidate,
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to remove the row',
        color: 'error',
      });
    },
  });
}

/**
 * Update a change set's name, description, or status. Invalidates both the
 * detail query and the list so the sidebar and reviews table stay in sync.
 */
export function useUpdateMetadataChangeSet() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    UpdateMetadataChangeSetResponse,
    Error,
    UpdateMetadataChangeSetInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.update(input),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.metadataChangeSets.get.queryKey({
          input: { id: input.id },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.metadataChangeSets.list.queryKey({
          input: {},
        }),
      });
      // Status/assignee/detail changes append change-set history events.
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.metadataChangeSets.listEvents.key(),
      });
      showToast({
        title: 'Change set updated',
        message: 'Your changes have been saved.',
        color: 'success',
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update the change set',
        color: 'error',
      });
    },
  });
}

/**
 * Delete a change set (and its staged changes). Invalidates the list so the
 * reviews table drops the removed row.
 */
export function useDeleteMetadataChangeSet(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<DeleteMetadataChangeSetResponse, Error, { id: string }>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.metadataChangeSets.delete(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.metadataChangeSets.list.queryKey({
          input: {},
        }),
      });
      showToast({
        title: 'Change set deleted',
        message: 'The change set has been deleted.',
        color: 'success',
      });
      onSuccess?.();
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to delete the change set',
        color: 'error',
      });
    },
  });
}
